// src/components/SiteDashboard.jsx
//
// Admin dashboard grouped by site (yacht / event) instead of a flat
// device list. Runs alongside the existing Dashboard - mount it on a
// separate route while evaluating.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetchJSON } from '../api/apiClient';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';
import LiveWanPanel from './LiveWanPanel';
import './SiteDashboard.css';
import '../styles/fleet-theme.css';
const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4'; // lives in public/, not src/images/ - needed for a stable, non-hashed URL the index.html preload tag can target

export const isPendingRoam = (d) => d.device_type === 'starlink' && d.service === 'roam';

export const SITE_TYPE_LABEL = {
  yacht: 'Yacht',
  event: 'Event',
  other: 'Site',
};

export const MGMT_LABEL = {
  fully_managed: 'FM',
  wan_managed: 'WM',
  data_center: 'DC',
  unknown: '?',
};

// Formats how long a device has been down, e.g. "2h 14m" / "3d 1h".
// Depends on `device.offline_since` (ISO timestamp) - a field that
// doesn't exist in the API yet. See the long comment above
// getSiteDownDuration below for exactly what the backend needs to add;
// this is written so the "DOWN" badge upgrades to "DOWN · 2h 14m"
// automatically the moment that field shows up, with zero further
// frontend changes.
export function formatDownDuration(sinceIso) {
  if (!sinceIso) return null;
  const since = new Date(sinceIso);
  if (isNaN(since.getTime())) return null;
  const ms = Date.now() - since.getTime();
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const remMins = mins % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${remMins}m`;
  return `${remMins}m`;
}

// Formats Starlink terminal ID by replacing the middle section with '…'
// Example: "01000000-00000000-007aec84" => "01000000-…-007aec84"
// Only applies if the string matches the pattern: 8-hex - 8-hex - 8-hex
// Plain lat/lng display for the site-list location line - no reverse
// geocoding here (unlike AdminSitesMap's popups), since this renders
// for every visible row at once rather than one lazily-opened popup at
// a time, and Nominatim's fair-use policy is 1 request/second. Raw
// coordinates are honest and cheap; "Near {city}" can be added later
// if it's worth the rate-limiting work for a whole list.
function formatLatLng(loc) {
  if (!loc || loc.lat == null || loc.lng == null) return null;
  const ns = loc.lat >= 0 ? 'N' : 'S';
  const ew = loc.lng >= 0 ? 'E' : 'W';
  return `${Math.abs(loc.lat).toFixed(2)}°${ns}, ${Math.abs(loc.lng).toFixed(2)}°${ew}`;
}

function formatStarlinkTerminalId(str) {
  if (!str || typeof str !== 'string') return str;
  
  // Check if it matches the pattern: 8 hex chars - 8 hex chars - 8 hex chars
  // Pattern: ^[0-9a-fA-F]{8}-[0-9a-fA-F]{8}-[0-9a-fA-F]{8}$
  const pattern = /^([0-9a-fA-F]{8})-([0-9a-fA-F]{8})-([0-9a-fA-F]{8})$/;
  const match = str.match(pattern);
  
  if (!match) {
    // If it doesn't match the pattern, return the original string
    return str;
  }
  
  // Return first part + '…' + last part
  return `${match[1]}-…-${match[3]}`;
}

// Formats uptime string into a shortened version with specified number of units
// Example: function_x("30 days 20 hours 1 minutes 35 seconds", 2) => "30d 20h"
// Example: function_x("21 hours 1 minutes 35 seconds", 2) => "21h 1m"
function formatUptime(uptimeStr, numUnits) {
  if (!uptimeStr) return '—';
  if (numUnits < 1) return '—';
  
  // Parse the uptime string
  const units = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  };
  
  // Match patterns like "30 days", "20 hours", "1 minutes", "35 seconds"
  const patterns = [
    { regex: /(\d+)\s*days?/, unit: 'days' },
    { regex: /(\d+)\s*hours?/, unit: 'hours' },
    { regex: /(\d+)\s*minutes?/, unit: 'minutes' },
    { regex: /(\d+)\s*seconds?/, unit: 'seconds' }
  ];
  
  let hasMatch = false;
  for (const pattern of patterns) {
    const match = uptimeStr.match(pattern.regex);
    if (match) {
      units[pattern.unit] = parseInt(match[1], 10);
      hasMatch = true;
    }
  }
  
  // If no matches found, return original string
  if (!hasMatch) return uptimeStr;
  
  // Build the result with the specified number of units
  const unitOrder = ['days', 'hours', 'minutes', 'seconds'];
  const unitLabels = {
    days: 'd',
    hours: 'h',
    minutes: 'm',
    seconds: 's'
  };
  
  const parts = [];
  let count = 0;
  
  for (const unit of unitOrder) {
    if (units[unit] > 0) {
      parts.push(`${units[unit]}${unitLabels[unit]}`);
      count++;
      if (count >= numUnits) break;
    }
  }
  
  // If no parts were added (all zero), return '0s' or similar
  if (parts.length === 0) return '0s';
  
  return parts.join(' ');
}

// A site is "fully down" once its LAST still-online device drops - so
// the site's own down-duration is the offline_since of whichever
// currently-offline device went offline most recently (not the first).
// Returns null (not a duration) if any device in the site is missing
// offline_since, rather than showing a wrong/partial number.
// 24H DATA: backend sends MB (Peplink hourly usage logs, last 24h).
// Binary units, same as the GB figures elsewhere (MB / 1024).
export function formatUsageMb(mb) {
  if (mb == null || !Number.isFinite(Number(mb))) return '—';
  const n = Number(mb);
  if (n < 1024) return `${n < 10 ? n.toFixed(1) : Math.round(n)} MB`;
  return `${(n / 1024).toFixed(n < 10240 ? 2 : 1)} GB`;
}

// ---------------------------------------------------------------------
// Starlink Maritime usage bar (mockups 8a desktop / 8b mobile).
//
// device.plan_usage comes from the backend for managed dishes:
//   { used_gb, client_gb, subscribed_gb? }
// subscribed_gb (what WE pay upstream) is only ever sent to admins - the
// client endpoint never includes the key - so its presence is what
// switches between the two labels:
//   admin   used/subscribed/client GB, bar = used of our subscription
//   client  used/client GB,             bar = used of the client's plan
// With no limit known, only the used figure is shown, without a bar.
// ---------------------------------------------------------------------
const fmtPlanGB = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return v >= 10 ? String(Math.round(v)) : String(Math.round(v * 10) / 10);
};

export function StarlinkUsageBar({ device, mobile = false }) {
  const u = device.plan_usage;
  if (!u) return null;

  const isAdminView = Object.prototype.hasOwnProperty.call(u, 'subscribed_gb');
  const limit = isAdminView ? (u.subscribed_gb || u.client_gb) : u.client_gb;
  const pct = limit ? Math.min(100, Math.max(0, (u.used_gb / limit) * 100)) : null;

  const label = isAdminView
    ? `${fmtPlanGB(u.used_gb)}/${fmtPlanGB(u.subscribed_gb)}/${fmtPlanGB(u.client_gb)}${mobile ? ' ' : ''}GB`
    : u.client_gb
    ? `${fmtPlanGB(u.used_gb)}/${fmtPlanGB(u.client_gb)}${mobile ? ' ' : ''}GB`
    : `${fmtPlanGB(u.used_gb)}${mobile ? ' ' : ''}GB`;

  const title = isAdminView
    ? `Used ${fmtPlanGB(u.used_gb)} GB · our subscription ${fmtPlanGB(u.subscribed_gb)} GB · client plan ${fmtPlanGB(u.client_gb)} GB`
    : `Used ${fmtPlanGB(u.used_gb)} GB${u.client_gb ? ` of ${fmtPlanGB(u.client_gb)} GB plan` : ''}`;

  const barH = mobile ? 5 : 4;
  const fill = device.online ? '#88F4EB' : 'rgba(255,255,255,.5)';

  return (
    <div
      title={title}
      style={{ display: 'flex', alignItems: 'center', gap: mobile ? 10 : 8, width: '100%', marginTop: mobile ? 12 : 0 }}
    >
      {pct != null && (
        <div style={{ flex: 1, height: barH, background: 'rgba(255,255,255,.12)' }}>
          <span style={{ width: `${pct}%`, height: barH, background: fill, display: 'block' }}></span>
        </div>
      )}
      <span
        style={{
          font: `400 ${mobile ? 11 : 10.5}px 'DM Mono',monospace`,
          color: device.online ? `rgba(255,255,255,${mobile ? '.72' : '.7'})` : 'rgba(255,255,255,.5)',
          whiteSpace: 'nowrap',
          marginLeft: pct == null ? 'auto' : undefined,
        }}
      >
        {label}
      </span>
    </div>
  );
}

export function getSiteDownSince(devices) {
  if (!devices.every((d) => d.offline_since)) return null;
  const timestamps = devices.map((d) => new Date(d.offline_since).getTime()).filter((t) => !isNaN(t));
  if (timestamps.length !== devices.length) return null;
  return new Date(Math.max(...timestamps)).toISOString();
}

export default function SiteDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [expandedDevice, setExpandedDevice] = useState(null); // device_key of the open live panel, or null
  const [expandedMode, setExpandedMode] = useState('wan'); // 'wan' | 'clients' - which section to show when opened
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Matches legacy Dashboard.jsx -> Modal.jsx: canMarkImportant and
  // canChangePriority are both gated by userRole === 'admin'. Same check
  // ClientSiteDashboard.jsx already applies on the client-facing side.
  const canEdit = user?.role === 'admin';

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [query, setQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState(null); // null = no active search; Set = matched device_keys
  const [searching, setSearching] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null); // null | 'fully_managed' | 'wan_managed' | 'data_center' | 'starlink_managed' | 'starlink_roam' | 'starlink_any' | 'needs_attention'
  const [sortMode, setSortMode] = useState('attention_first'); // 'attention_first' | 'default' - matches the mockup's default sort
  const searchDebounce = useRef(null);

  const [summary, setSummaryState] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE_SIZE = 12;

  // Sites render as soon as the first page arrives - this is the
  // "immediate view" fix. Fleet-wide summary stats are fetched
  // separately and still take the same time they always did (that
  // computation doesn't get cheaper just because fewer site ROWS are
  // shown at once) - the stat cards show their own loading state
  // instead of blocking the whole page.
  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const first = await apiFetchJSON(`/api/v2/admin/sites-page?offset=0&limit=${PAGE_SIZE}`);
      setData({ sites: first.sites || [] });
      setLoading(false);

      // Keep pulling subsequent pages in the background until done.
      let offset = first.sites.length;
      let hasMore = first.has_more;
      setLoadingMore(hasMore);

      while (hasMore) {
        const next = await apiFetchJSON(`/api/v2/admin/sites-page?offset=${offset}&limit=${PAGE_SIZE}`);
        setData((prev) => ({ sites: [...(prev?.sites || []), ...(next.sites || [])] }));
        offset += next.sites.length;
        hasMore = next.has_more;
      }
      setLoadingMore(false);
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const [unassigned, setUnassignedState] = useState({ peplink: [], starlink: [] });

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      // The summary computation scans the whole fleet regardless of
      // this flag - getting the raw unassigned list in the same call
      // avoids a second, equally expensive fetch just for that list.
      const json = await apiFetchJSON('/api/v2/admin/dashboard?include_unassigned=true');
      setSummaryState(json.summary || { peplink: {}, starlink: {} });
      setUnassignedState(json.unassigned || { peplink: [], starlink: [] });
    } catch {
      // Non-fatal - stat cards just show nothing rather than erroring
      // the whole page out over a summary-only failure.
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Per-site location, reusing the exact same data source and
  // Starlink-Maritime-only rule AdminSitesMap.jsx already uses -
  // /api/v2/admin/sites-map returns location=None for any site whose
  // only devices are Peplink or Starlink ROAM (no ELCOME cache entry),
  // so a site with nothing here genuinely has nothing to show, not a
  // fetch that hasn't resolved yet. Fetched once, independently of the
  // paginated site list, same as the map page does it.
  const [siteLocations, setSiteLocations] = useState({});

  const loadSiteLocations = useCallback(async () => {
    try {
      const json = await apiFetchJSON('/api/v2/admin/sites-map');
      const map = {};
      (json.sites || []).forEach((s) => {
        if (s.location) map[s.site_id] = s.location;
      });
      setSiteLocations(map);
    } catch {
      // Non-fatal - rows just show no location line rather than
      // erroring the whole dashboard out over this.
    }
  }, []);

  // Reverse-geocoded "Near {city}" labels, same Nominatim call
  // AdminSitesMap.jsx uses for its popups - but that page only ever
  // geocodes one site at a time (whichever popup is open). This page
  // can have dozens of located sites visible at once, so requests are
  // serialized through one queue with a fixed gap between them instead
  // of firing in parallel, to actually stay within Nominatim's 1
  // request/second fair-use policy rather than assume the count stays
  // low. Cached by site_id so re-renders and re-fetches of
  // siteLocations don't re-queue a site that's already resolved (or
  // already in flight).
  const geocodeCacheRef = useRef({});
  const geocodeQueueRef = useRef(Promise.resolve());
  const [locationLabels, setLocationLabels] = useState({});

  const enqueueGeocode = useCallback((siteId, lat, lng) => {
    if (geocodeCacheRef.current[siteId] !== undefined) return; // resolved or in flight already
    geocodeCacheRef.current[siteId] = null;

    geocodeQueueRef.current = geocodeQueueRef.current.then(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=10`,
          { headers: { Accept: 'application/json' } }
        );
        const data = await res.json();
        const addr = data?.address || {};
        const place = addr.city || addr.town || addr.village || addr.county || addr.state;
        const label = place ? `Near ${place}${addr.country_code ? ', ' + addr.country_code.toUpperCase() : ''}` : 'At sea';
        geocodeCacheRef.current[siteId] = label;
        setLocationLabels((prev) => ({ ...prev, [siteId]: label }));
      } catch {
        delete geocodeCacheRef.current[siteId]; // network hiccup - allow a retry on the next pass
      }
      // Stay under Nominatim's 1 request/second fair-use limit before
      // the next queued site fires.
      await new Promise((r) => setTimeout(r, 1100));
    });
  }, []);

  useEffect(() => {
    Object.entries(siteLocations).forEach(([siteId, loc]) => {
      enqueueGeocode(siteId, loc.lat, loc.lng);
    });
  }, [siteLocations, enqueueGeocode]);

  useEffect(() => { loadSiteLocations(); }, [loadSiteLocations]);

  // Deep-link support: Sites Map's "OPEN SITE ->" navigates here with
  // ?site={site_id}. Once that site shows up in whatever page has
  // loaded so far, expand it and scroll it into view - matches how the
  // Sites Map popup promised "open + expand" would behave. Only runs
  // once per query value (targetSiteRef), since sites load in pages and
  // this effect re-fires as each page arrives.
  const targetSiteRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetSiteId = params.get('site');
    if (!targetSiteId || targetSiteId === targetSiteRef.current) return;

    const match = (data?.sites || []).find((s) => String(s.site_id) === targetSiteId);
    if (!match) return; // not loaded yet - effect re-fires as more pages arrive

    targetSiteRef.current = targetSiteId;
    setExpanded((prev) => ({ ...prev, [match.site_id]: true }));
    // Let the newly-expanded row render before scrolling to it.
    setTimeout(() => {
      document.querySelector(`[data-site-row="${match.site_id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, [data, location.search]);

  // Debounced real search - hits the backend (serial/model/name/firmware/
  // ICCID/IMEI/IMSI for Peplink, kit/dish/service-line for Starlink)
  // rather than only matching what's already loaded client-side.
  useEffect(() => {
    const q = query.trim();
    if (searchDebounce.current) clearTimeout(searchDebounce.current);

    if (q.length < 2) {
      setSearchMatches(null);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchDebounce.current = setTimeout(async () => {
      try {
        const res = await apiFetchJSON(`/api/v2/admin/search?q=${encodeURIComponent(q)}`);
        setSearchMatches(new Set(res.matched_device_keys || []));
      } catch {
        setSearchMatches(new Set()); // fail closed - show nothing rather than everything
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchDebounce.current);
  }, [query]);

  const toggle = (siteId) =>
    setExpanded((prev) => ({ ...prev, [siteId]: !prev[siteId] }));

  const sites = data?.sites || [];
  // summary/unassigned now come from their own dedicated state
  // (loadSummary), decoupled from the paginated site list.

  // A site counts as "down" when it has at least one device and every
  // device at it is offline (Peplink + Starlink together).
  const isSiteDown = (s) => s.devices.length > 0 && s.devices.every((d) => d.online === false);

  // NOTE: computed from whatever site pages have loaded so far, not a
  // backend-aggregated total like the other stat cards (Sites Monitored,
  // Peplink Online, etc. all come pre-summed from /dashboard). While
  // loadingMore is still true this number can undercount. If this needs
  // to be exact at all times, it should move server-side into the
  // summary endpoint the same way the other cards' numbers already are.
  const downSites = sites.filter(isSiteDown);
  const sitesDownCount = downSites.length;

  // Counts for the filter pills - computed from whatever pages have
  // loaded so far, same caveat as sitesDownCount above (not yet a
  // backend-aggregated total).
  const attentionSitesCount = sites.filter((s) =>
    s.devices.some(
      (d) => d.online === false && !isPendingRoam(d))
  ).length;
  const starlinkSitesCount = sites.filter((s) => s.devices.some((d) => d.device_type === 'starlink')).length;

  const matchesFilter = (d, site) => {
    if (!activeFilter) return true;
    if (activeFilter === 'sites_down') return isSiteDown(site);
    if (activeFilter === 'starlink_managed') return d.device_type === 'starlink' && d.service === 'managed';
    if (activeFilter === 'starlink_roam') return d.device_type === 'starlink' && d.service === 'roam';
    if (activeFilter === 'starlink_any') return d.device_type === 'starlink';
    if (activeFilter === 'needs_attention') return d.online === false;
    return d.device_type === 'peplink' && d.peplinkManagementType === activeFilter;
  };

  const matchesSearch = (d) => !searchMatches || searchMatches.has(d.device_key);

  // When the search text matches the site's own name, show every device
  // at that site (e.g. "Hana" also shows its Starlink kits, whose names
  // don't contain "Hana"). Uses the same 2-character minimum as the
  // backend search, so short input doesn't match every site.
  const q = query.trim().toLowerCase();
  const siteMatchesSearch = (s) =>
    !!searchMatches && q.length >= 2 && (s.site_name || '').toLowerCase().includes(q);

  let visibleSites = sites
    .map((s) => {
      const wholeSite = siteMatchesSearch(s);
      return {
        ...s,
        devices: s.devices.filter((d) => matchesFilter(d, s) && (wholeSite || matchesSearch(d))),
      };
    })
    .filter((s) => !searchMatches && !activeFilter ? true : s.devices.length > 0);

  if (sortMode === 'attention_first') {
    // Sites with at least one offline device first, otherwise preserve
    // original order within each group (stable sort).
    visibleSites = visibleSites
      .map((s, i) => ({ s, i, hasOffline: s.devices.some((d) => d.online === false && !isPendingRoam(d)) }))
      .sort((a, b) => (b.hasOffline - a.hasOffline) || (a.i - b.i))
      .map((x) => x.s);
  }

  // Generic row click (name/caret/badge area) - goes straight to the
  // historical monitoring page, matching the same behavior already
  // built for the client Site Dashboard.
  const handleRowClick = (d) => {
    if (d.device_type === 'peplink') {
      navigate(`/monitoring/${d.device_key}`);
    } else if (d.device_type === 'starlink') {
      if (d.service === 'roam' || d.service === 'missing') return; // no telemetry for ROAM; MISSING has left ELCOME
      navigate(`/admin/starlink/${d.device_key}`);
    }
  };

  // WAN/Client stat clicks still open the live panel - this is still the
  // only place WAN priority/mark-important editing happens (monitoring
  // is deliberately read-only), so this path has to stay.
  const handleStatClick = (d, mode) => {
    if (d.device_type !== 'peplink') return;
    if (expandedDevice === d.device_key) {
      if (expandedMode === mode) {
        setExpandedDevice(null);
      } else {
        setExpandedMode(mode);
      }
    } else {
      setExpandedDevice(d.device_key);
      setExpandedMode(mode);
    }
  };

  const unassignedDevices = activeFilter === 'sites_down'
    ? []
    : [
        ...unassigned.peplink,
        // The dashboard endpoint names the parent router `behind_peplink`
        // for unassigned Starlinks, while sites-page uses
        // `connected_via_peplink`. Map it across so DeviceRow shows the
        // same "via <router> (<WAN>)" line in both lists.
        ...unassigned.starlink.map((d) => ({
          ...d,
          connected_via_peplink: d.connected_via_peplink ?? d.behind_peplink,
        })),
      ].filter((d) => matchesFilter(d, null) && matchesSearch(d));

  // ---- Guided tour ("?" in FleetHeader) ---------------------------------
  // Shows a real site + device. Prefer a site with an ONLINE Peplink
  // router, so the live WAN / CLIENTS step points at something that works.
  const tourSite =
    visibleSites.find((s) => s.devices.some((d) => d.device_type === 'peplink' && d.online)) ||
    visibleSites.find((s) => s.devices.some((d) => d.device_type === 'peplink')) ||
    visibleSites.find((s) => s.devices.length > 0) ||
    visibleSites[0] || null;
  const tourDevice = tourSite
    ? (tourSite.devices.find((d) => d.device_type === 'peplink' && d.online) ||
       tourSite.devices.find((d) => d.device_type === 'peplink') || tourSite.devices[0] || null)
    : null;
  const tourSnapshotRef = useRef(null);
  usePageTour('site-dashboard', {
    isMobile,
    siteCount: summary?.total_sites || sites.length,
    sitesDownCount,
    attentionSitesCount,
    starlinkSitesCount,
    tourSite,
    tourDevice,
    canEdit,
    unassignedCount: unassignedDevices.length,
    expandTourSite: () => tourSite && setExpanded((prev) => ({ ...prev, [tourSite.site_id]: true })),
    onTourStart: () => {
      tourSnapshotRef.current = { expanded, expandedDevice };
      setExpandedDevice(null); // close any live panel so the page is in a clean state
    },
    onTourEnd: () => {
      if (!tourSnapshotRef.current) return;
      setExpanded(tourSnapshotRef.current.expanded);
      setExpandedDevice(tourSnapshotRef.current.expandedDevice);
    },
  }, { ready: !loading && !error });

  // Early returns live below every hook (React rules of hooks).
  if (loading) {
    return (
        <div className="cm-loading-overlay">
          <div className="cm-spinner-container">
            <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
              <source src={loadingVideo} type="video/mp4" />
            </video>
            <p>Loading sites…</p>
          </div>
        </div>
    );
  }

  return (
    <div className="sd-page">
      <FleetHeader
        title="Site Dashboard"
        user={user}
        onLogout={onLogout}
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="serial · model · ICCID · IMSI · UT ID · site"
      />

      <div className="sd-wrap">
        {/* Same banner markup as DeviceManager's message banner. The page
            still renders underneath, so a failure on a later page keeps
            the sites that already loaded instead of wiping them out. */}
        {error && (
          <div className="ft-toolbar-error" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        {searching && <div className="sd-searching">Searching…</div>}

        {/* Mockup 8b's mobile screen has no stat-card row at all - just
            the header, then the pill row, then the site list straight
            away. The desktop stat cards (mockup 8a) stay exactly as
            they were; this only changes what renders under 768px. */}
        {!isMobile && (
          <CounterFilterBar
            summary={summary}
            summaryLoading={summaryLoading}
            totalSites={summary?.total_sites || sites.length}
            sitesDownCount={sitesDownCount}
            attentionSitesCount={attentionSitesCount}
            activeFilter={activeFilter}
            onFilterClick={(f) => setActiveFilter((prev) => (prev === f ? null : f))}
          />
        )}

        <div className="ft-filter-row" data-tour="sd-pills">
          {isMobile ? (
            <>
              {/* Mobile pill row per mockup 8b: ATTENTION / DOWN / ALL -
                  replaces the desktop's ATTENTION / ALL / STARLINK set.
                  Same ft-pill anatomy and the same underlying state
                  (sortMode / activeFilter) already used elsewhere in
                  this file, just mapped onto the three labels the
                  mockup actually shows for this screen. */}
              <button
                className={'ft-pill' + (sortMode === 'attention_first' ? ' active' : '')}
                onClick={() => setSortMode('attention_first')}
              >
                ATTENTION {attentionSitesCount}
              </button>
              <button
                className={'ft-pill' + (activeFilter === 'sites_down' ? ' active' : '')}
                onClick={() => setActiveFilter((prev) => (prev === 'sites_down' ? null : 'sites_down'))}
              >
                DOWN {sitesDownCount}
              </button>
              <button
                className={'ft-pill' + (sortMode === 'default' && !activeFilter ? ' active' : '')}
                onClick={() => { setSortMode('default'); setActiveFilter(null); }}
              >
                ALL {sites.length}
              </button>
            </>
          ) : (
            <>
              <button
                className={'ft-pill' + (sortMode === 'attention_first' ? ' active' : '')}
                onClick={() => setSortMode('attention_first')}
              >
                ATTENTION · {attentionSitesCount}
              </button>
              <button
                className={'ft-pill' + (sortMode === 'default' && !activeFilter ? ' active' : '')}
                onClick={() => { setSortMode('default'); setActiveFilter(null); }}
              >
                ALL · {sites.length}
              </button>
              <button
                className={'ft-pill' + (activeFilter === 'starlink_any' ? ' active' : '')}
                onClick={() => setActiveFilter((prev) => (prev === 'starlink_any' ? null : 'starlink_any'))}
              >
                STARLINK · {starlinkSitesCount}
              </button>
              {/* "Over Plan" from the mockup isn't here yet - it needs live
                  per-device data-plan usage, which the lightweight fleet
                  list doesn't have (only the individual Starlink detail
                  page fetches that live). Not faking a filter that can't
                  actually work correctly. */}
            </>
          )}
        </div>

        <div className="ft-grid">
        <div className="ft-grid-left">

        {/* data is null when the first page failed - don't claim
            "No sites yet" when we simply couldn't load them. */}
        {data && visibleSites.length === 0 && (
          <div className="sd-empty">
            No sites yet. Create one in Site Manager, then assign devices to it.
          </div>
        )}

        {visibleSites.length > 0 && (
          <div className="sd-fleet-table">
            {!isMobile && (
              <div className="sd-fleet-head-row">
                <div></div>
                <div>SITE</div>
                <div>STATE</div>
                <div>DEVICES</div>
                <div className="ft-num">ONLINE</div>
                <div>NEEDS ATTENTION</div>
                <div className="ft-num">ALERTS</div>
              </div>
            )}
              {visibleSites.map((site) => {
                const peplinks = site.devices.filter((d) => d.device_type === 'peplink');
                const starlinks = site.devices.filter((d) => d.device_type === 'starlink');
                const starlinks_roam = site.devices.filter((d) => d.device_type === 'starlink' && d.service === 'roam');
                const starlinks_maritime = site.devices.filter((d) => d.device_type === 'starlink' && d.service === 'managed');

                const allDevices = site.devices;
                const countedDevices = allDevices.filter((d) => !isPendingRoam(d));
                const offlineCount = countedDevices.filter((d) => d.online === false).length;

                let state = 'unknown';
                if (countedDevices.length > 0) {
                  if (offlineCount === countedDevices.length) state = 'down';
                  else if (offlineCount > 0) state = 'degraded';
                  else state = 'online';
                }

                // "WAN Health" note: the mockup's per-site WAN Health
                // column isn't in this rebuild - the site-level ONLINE
                // fraction below covers total device health, and
                // per-important-WAN health is shown at the individual
                // device row level (which does have that data).

                const siteDownSince = state === 'down' ? getSiteDownSince(allDevices) : null;
                const downDuration = formatDownDuration(siteDownSince);

                const stateBadge = allDevices.length === 0 ? (
                  <span className="ft-badge ft-badge-unknown">NO DEVICES</span>
                ) : (
                  <span className={'ft-badge ft-badge-' + (state === 'down' ? 'down' : state === 'degraded' ? 'degraded' : 'online')}>
                    {state === 'down' ? `DOWN${downDuration ? ` · ${downDuration}` : ''}` : state === 'degraded' ? 'DEGRADED' : 'ONLINE'}
                  </span>
                );

                const stateColor =
                  state === 'down' ? '#fff' :
                  state === 'degraded' ? 'var(--color-accent)' :
                  state === 'online' ? 'var(--color-secondary)' : 'rgba(255,255,255,.3)';

                // "Needs attention" narrative - built only from signals the
                // fleet list actually carries (offline Starlink lines,
                // offline *important* clients/WAN), same honesty rule as
                // the rest of this file. Falls back to a plain offline
                // count rather than inventing a cause when none of those
                // specific signals explain the degraded state.
                
                //const offlineStarlinkCount = starlinks.filter((d) => d.online === false).length;
                const offlineStarlinkMaritimeCount = starlinks.filter((d) => d.online === false && d.service === 'managed').length;
                const offlineImportantClients = allDevices.reduce((s, d) => s + (d.offline_important_clients_count || 0), 0);
                const offlineImportantWan = allDevices.reduce((s, d) => s + (d.offline_important_wan_count || 0), 0);

                let attentionText;
                if (allDevices.length === 0) {
                  attentionText = 'No devices assigned';
                } else if (state === 'down') {
                  attentionText = `All devices unreachable${siteDownSince ? ` — last contact ${new Date(siteDownSince).toUTCString().slice(17, 22)} UTC` : ''}`;
                } else if (state === 'degraded') {
                  const parts = [];
                  if (offlineStarlinkMaritimeCount > 0) parts.push(`${offlineStarlinkMaritimeCount} Starlink Maritime line${offlineStarlinkMaritimeCount > 1 ? 's' : ''} offline`);
                  if (offlineImportantClients > 0) parts.push(`${offlineImportantClients} important client${offlineImportantClients > 1 ? 's' : ''} offline`);
                  if (offlineImportantWan > 0) parts.push(`${offlineImportantWan} important WAN${offlineImportantWan > 1 ? 's' : ''} offline`);
                  attentionText = parts.length > 0 ? parts.join(' · ') : `${offlineCount} device${offlineCount > 1 ? 's' : ''} offline`;
                } else {
                  attentionText = 'Nothing outstanding';
                }

                // Alerts counter - number of distinct flagged conditions
                // (not a repeat of any other column), so a site that's
                // fully down still reads as "alerts" rather than 0.
                const alertCount = state === 'down'
                  ? allDevices.length
                  : (offlineStarlinkMaritimeCount > 0 ? 1 : 0) + (offlineImportantClients > 0 ? 1 : 0) + (offlineImportantWan > 0 ? 1 : 0);

                return (
                  <React.Fragment key={site.site_id}>
                    {isMobile ? (
                      // Mockup 8b: a DOWN site card carries its faint
                      // background tint whether or not it's expanded;
                      // a DEGRADED card only gets it while expanded.
                      // (Compare the mockup's "Ab Initiio" - expanded,
                      // degraded, tinted - against "Allure" - collapsed,
                      // degraded, no tint - and "Boldyn" - collapsed,
                      // down, tinted anyway.)
                      <div
                        className="sd-fleet-row-mobile"
                        style={{
                          borderLeft: `2px solid ${stateColor}`,
                          background:
                            state === 'down'
                              ? 'rgba(255,255,255,.035)'
                              : state === 'degraded' && expanded[site.site_id]
                              ? 'rgba(202,251,109,.05)'
                              : 'transparent',
                        }}
                        data-site-row={site.site_id}
                        data-tour={tourSite && site.site_id === tourSite.site_id ? 'sd-site' : undefined}
                        onClick={() => toggle(site.site_id)}
                      >
                        <div className="sd-fleet-card-top">
                          <span className={'sd-caret' + (expanded[site.site_id] ? ' open' : '')} style={{ color: expanded[site.site_id] ? stateColor : 'rgba(255,255,255,.4)' }}>
                            {expanded[site.site_id] ? '▾' : '▸'}
                          </span>
                          <span className="sd-site-name sd-site-name-tight">{site.site_name}</span>
                          <span className={'sd-type-badge ' + site.site_type}>
                            {SITE_TYPE_LABEL[site.site_type] || site.site_type}
                          </span>
                          <span className="sd-fleet-card-state">{stateBadge}</span>
                        </div>
                        {siteLocations[site.site_id] && (
                          <div className="sd-site-location sd-site-location-mobile">
                            {locationLabels[site.site_id] || formatLatLng(siteLocations[site.site_id])}
                          </div>
                        )}
                        <div className="sd-fleet-card-chips">
                          {peplinks.length > 0 && <span className="sd-fleet-chip">{peplinks.length} PEPLINK</span>}
                          {starlinks_maritime.length > 0 && <span className="sd-fleet-chip">{starlinks_maritime.length} MARITIME</span>}
                          {starlinks_roam.length > 0 && <span className="sd-fleet-chip">{starlinks_roam.length} ROAM</span>}
                          <span className="sd-fleet-online-frac" style={{ color: stateColor }}>
                            {allDevices.length - offlineCount}/{allDevices.length} online
                          </span>
                        </div>
                      </div>
                    ) : (
                    <div
                      className="sd-fleet-grid-row"
                      style={{ borderLeft: `2px solid ${stateColor}`, background: expanded[site.site_id] ? (state === 'down' ? 'rgba(255,255,255,.035)' : state === 'degraded' ? 'rgba(202,251,109,.05)' : 'transparent') : 'transparent' }}
                      data-site-row={site.site_id}
                      data-tour={tourSite && site.site_id === tourSite.site_id ? 'sd-site' : undefined}
                      onClick={() => toggle(site.site_id)}
                    >
                      <div className={'sd-caret' + (expanded[site.site_id] ? ' open' : '')} style={{ color: expanded[site.site_id] ? stateColor : 'rgba(255,255,255,.4)' }}>
                        {expanded[site.site_id] ? '▾' : '▸'}
                      </div>
                      <div className="sd-fleet-site-cell">
                        <div className="sd-fleet-site-name-row">
                          <span className="sd-site-name">{site.site_name}</span>
                          <span className={'sd-type-badge ' + site.site_type}>
                            {SITE_TYPE_LABEL[site.site_type] || site.site_type}
                          </span>
                        </div>
                        {siteLocations[site.site_id] && (
                          <div className="sd-site-location">
                            {locationLabels[site.site_id] || formatLatLng(siteLocations[site.site_id])}
                          </div>
                        )}
                      </div>
                      <div>{stateBadge}</div>
                      <div className="sd-fleet-devices-cell">
                          {peplinks.length > 0 && <span className="sd-devices-chip">{peplinks.length} PEPLINK</span>}
                          {starlinks_maritime.length > 0 && <span className="sd-devices-chip">{starlinks_maritime.length} MARITIME</span>}
                          {starlinks_roam.length > 0 && <span className="sd-devices-chip">{starlinks_roam.length} ROAM</span>}
                      </div>
                      <div className="ft-num" style={{ color: stateColor }}>
                        {allDevices.length - offlineCount}/{allDevices.length}
                      </div>
                      <div className="sd-fleet-attention-cell" style={{ color: state === 'down' ? '#fff' : state === 'online' ? 'rgba(255,255,255,.45)' : 'rgba(255,255,255,.72)' }}>
                        {attentionText}
                      </div>
                      <div className="ft-num">
                        {allDevices.length === 0 ? (
                          <span style={{ color: 'rgba(255,255,255,.3)' }}>0</span>
                        ) : alertCount > 0 ? (
                          <span
                            className="sd-fleet-alert-chip"
                            style={
                              state === 'down'
                                ? { background: '#fff', color: 'var(--color-primary)' }
                                : { background: 'rgba(202,251,109,.18)', color: 'var(--color-accent)' }
                            }
                          >
                            {alertCount}
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,.3)' }}>0</span>
                        )}
                      </div>
                    </div>
                    )}


                    {expanded[site.site_id] && (
                      <div className="sd-fleet-expand-row">
                        <div className="sd-device-list">
                          {site.devices.length === 0 && (
                            <div className="sd-no-devices">No devices assigned yet</div>
                          )}
                          {!isMobile && site.devices.length > 0 && (
                            <div className="sd-device-grid-head">
                              <div>DEVICE</div>
                              <div>STATE</div>
                              <div>WANS</div>
                              <div>CLIENTS</div>
                              <div className="ft-num">24H DATA</div>
                              <div className="ft-num">UPTIME</div>
                            </div>
                          )}
                          {site.devices.map((d) => (
                            <React.Fragment key={d.device_type + d.device_key}>
                              <DeviceRow
                                device={d}
                                onClick={() => handleRowClick(d)}
                                onOpenMode={(mode) => handleStatClick(d, mode)}
                                isOpen={expandedDevice === d.device_key}
                                isMobile={isMobile}
                                tourId={tourDevice && site.site_id === tourSite?.site_id && d.device_key === tourDevice.device_key ? 'sd-device' : undefined}
                              />
                              {d.device_type === 'peplink' && expandedDevice === d.device_key && (
                                <LiveWanPanel device={d} initialMode={expandedMode} canEdit={canEdit} onClose={() => setExpandedDevice(null)} />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
          </div>
        )}


        {loadingMore && (
          <div className="ft-loading-more">Loading more sites…</div>
        )}

        {/* Unassigned devices - always visible, not hidden behind a
            toggle. No client, no site yet, but still clickable. */}
        {unassignedDevices.length > 0 && (
          <div className="sd-site sd-site-unassigned" data-tour="sd-unassigned">
            <div
              className="sd-site-header"
              onClick={() => setExpanded((prev) => ({ ...prev, __unassigned: !prev.__unassigned }))}
            >
              <div className="sd-site-title">
                <span className={'sd-caret' + (expanded.__unassigned ? ' open' : '')}>▸</span>
                <span className="sd-site-name">Unassigned</span>
                <span className="sd-type-badge other">
                  {unassignedDevices.length} device{unassignedDevices.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {expanded.__unassigned && (
              <div className="sd-device-list">
                {!isMobile && unassignedDevices.length > 0 && (
                  <div className="sd-device-grid-head">
                    <div>DEVICE</div>
                    <div>STATE</div>
                    <div>WANS</div>
                    <div>CLIENTS</div>
                    <div className="ft-num">24H DATA</div>
                    <div className="ft-num">UPTIME</div>
                  </div>
                )}
                {unassignedDevices.map((d) => (
                  <React.Fragment key={d.device_type + d.device_key}>
                    <DeviceRow
                      device={d}
                      onClick={() => handleRowClick(d)}
                      onOpenMode={(mode) => handleStatClick(d, mode)}
                      isOpen={expandedDevice === d.device_key}
                      isMobile={isMobile}
                    />
                    {d.device_type === 'peplink' && expandedDevice === d.device_key && (
                      <LiveWanPanel device={d} initialMode={expandedMode} canEdit={canEdit} onClose={() => setExpandedDevice(null)} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}

        </div>

        <div className="ft-grid-right">
          <EventFeedSidebar isMobile={isMobile} />
        </div>

        </div>
      </div>
    </div>
  );
}


// tourId (optional): guided-tour tag. Puts data-tour={tourId} on the row
// and data-tour={tourId + '-stats'} on its WAN / CLIENTS stats.
export function DeviceRow({ device, onClick, onOpenMode, isOpen, isMobile, tourId }) {
  const isRoam = device.device_type === 'starlink' && device.service === 'roam';
  const isExpandable = device.device_type === 'peplink';
  // Separate from isExpandable - the desktop grid row also needs to be
  // clickable for non-roam Starlink devices (navigates to the Starlink
  // device page), even though only Peplink expands the LiveWanPanel
  // inline below it. Roam devices genuinely have no click target (no
  // usage/telemetry API exists for them), so they stay non-clickable.
  // MISSING = was on ELCOME, no longer returned by it (no service line
  // any more). No detail page to open, and its online state is unknown.
  const isMissing = device.device_type === 'starlink' && device.service === 'missing';
  const isClickable = isExpandable || (device.device_type === 'starlink' && !isRoam && !isMissing);
  const isPeplinkMobile = isMobile && device.device_type === 'peplink';
  const isDesktop = !isMobile;
  // Shared by the desktop grid row and the mobile card below - same
  // two-color ramp (turquoise/white) used everywhere else for device
  // state, no red/amber/pink kind-based color anywhere in this file.
  const statusColor = device.online ? 'var(--color-secondary)' : '#fff';

  // STATE badge, shared by desktop and mobile.
  // ROAM has no Starlink API, so its state comes from the LED of the
  // Peplink WAN it's plugged into (backend: status_led, online = green):
  // green -> ONLINE, red -> OFFLINE, anything else / not behind a
  // Peplink -> "-". MISSING stays "-".
  const roamOffline = isRoam && !device.online && device.status_led === 'red';
  const stateLabel = device.online
    ? 'ONLINE'
    : roamOffline
    ? 'OFFLINE'
    : isRoam || isMissing
    ? '-'
    : `OFFLINE${formatDownDuration(device.offline_since) ? ` ${formatDownDuration(device.offline_since)}` : ''}`;
  const stateBadge = (
    <span
      className={'ft-badge ' + (device.online ? 'ft-badge-online' : 'ft-badge-down')}
      title={isRoam && device.wan_status ? `Peplink WAN: ${device.wan_status}` : undefined}
    >
      {stateLabel}
    </span>
  );

  const openMode = (mode) => (e) => {
    e.stopPropagation();
    onOpenMode && onOpenMode(mode);
  };

  // Desktop: the WAN / CLIENTS cells are small, so a click slightly above,
  // below or beside the number used to land on the row and open the
  // Monitoring page instead. The whole COLUMN of the row (full row height,
  // full column width) now opens the LiveWanPanel. The cells stretch to
  // their full column width; the row's own padding above/below is covered
  // by checking which column the click's X position falls in.
  const wanCellRef = useRef(null);
  const clientsCellRef = useRef(null);
  const handleDesktopRowClick = (e) => {
    if (device.device_type === 'peplink' && onOpenMode) {
      const x = e.clientX;
      const inCell = (ref) => {
        const r = ref.current && ref.current.getBoundingClientRect();
        return !!r && r.width > 0 && x >= r.left && x <= r.right;
      };
      if (inCell(wanCellRef)) { e.stopPropagation(); onOpenMode('wan'); return; }
      if (inCell(clientsCellRef)) { e.stopPropagation(); onOpenMode('clients'); return; }
    }
    if (isClickable && onClick) onClick(e);
  };

  // Desktop grid row - matches mockup 8a's DEVICE / STATE / WAN HEALTH /
  // CLIENTS / 24H DATA / UPTIME columns exactly, for BOTH device types
  // (the mockup shows Peplink and Starlink devices in the same grid,
  // not two different layouts). 24H DATA comes from usage_24h_mb
  // (Peplink hourly usage logs; per-WAN for a Starlink behind a Peplink)
  // and shows "—" when there's no recent data.
  if (isDesktop) {
    const importantWan = device.important_wan_count || 0;
    const offlineImportantWan = device.offline_important_wan_count || 0;

    const secondaryChip = device.device_type === 'peplink'
      ? (MGMT_LABEL[device.peplinkManagementType] || null)
      : (isRoam ? 'ROAM' : isMissing ? 'MISSING' : device.service === 'managed' ? 'MARITIME' : null);

    const metaLine = device.device_type === 'peplink'
      ? [device.serial_number, device.model].filter(Boolean).join(' · ')
      : (device.connected_via_peplink ? `via ${device.connected_via_peplink}${device.wan_interface_name ? ` (${device.wan_interface_name})` : ''}` : (device.serial_number || ''));

    return (
      <div
        className={'sd-device-grid-row' + (isOpen ? ' sd-device-grid-row-open' : '') + (isClickable ? ' sd-device-grid-row-clickable' : '')}
        data-tour={tourId}
        style={{ borderLeft: `2px solid ${statusColor}` }}
        onClick={isClickable ? handleDesktopRowClick : undefined}
      >
        <div className="sd-device-grid-name-cell">
          <div className="sd-device-grid-name-row">
            <span className="sd-device-grid-name" title={device.name}>{formatStarlinkTerminalId(device.name)}</span>
            <span className="sd-devices-chip">{device.device_type === 'peplink' ? 'PEPLINK' : 'STARLINK'}</span>
            {secondaryChip && <span className="sd-devices-chip">{secondaryChip}</span>}
          </div>
          {metaLine && <div className="sd-device-grid-meta">{metaLine}</div>}
        </div>

        <div>{stateBadge}</div>

        <div
          ref={wanCellRef}
          data-tour={tourId && device.device_type === 'peplink' ? `${tourId}-stats` : undefined}
          className="sd-device-grid-clients"
          style={{ cursor: device.device_type === 'peplink' ? 'pointer' : 'default', justifySelf: 'stretch' }}
          onClick={device.device_type === 'peplink' ? openMode('wan') : undefined}
          title={device.device_type === 'peplink' ? 'Click to view WAN' : undefined}
        >
          {device.device_type === 'peplink' ? (
            <>
              <span>{device.wan_interface_count ?? '—'}</span>
              {importantWan > 0 && (
                <>
                  <span className="sd-device-grid-clients-sep">·</span>
                  <span>{importantWan} imp</span>
                </>
              )}
              {offlineImportantWan > 0 && (
                <span className="sd-device-grid-off-chip">{offlineImportantWan} OFF</span>
              )}
            </>
          ) : (
            <span className="sd-na">n/a · uplink</span>
          )}
        </div>

        <div ref={clientsCellRef} className="sd-device-grid-clients" style={{ cursor: device.device_type === 'peplink' ? 'pointer' : 'default', justifySelf: 'stretch' }} onClick={device.device_type === 'peplink' ? openMode('clients') : undefined} title={device.device_type === 'peplink' ? 'Click to view clients' : undefined}>
          {device.device_type === 'peplink' ? (
            <>
              <span>{device.client_count ?? '—'}</span>
              {device.important_clients_count > 0 && (
                <>
                  <span className="sd-device-grid-clients-sep">·</span>
                  <span>{device.important_clients_count} imp</span>
                </>
              )}
              {device.offline_important_clients_count > 0 && (
                <span className="sd-device-grid-off-chip">{device.offline_important_clients_count} OFF</span>
              )}
            </>
          ) : (
            device.plan_usage
              ? <StarlinkUsageBar device={device} />
              : <span className="sd-na">—</span>
          )}
        </div>

        <div className={'ft-num' + (device.usage_24h_mb == null ? ' sd-na' : '')} style={{ fontSize: 12 }} title={device.usage_24h_mb != null ? 'Last 24 hours, from the Peplink usage logs' : undefined}>
          {formatUsageMb(device.usage_24h_mb)}
        </div>

        <div className="ft-num sd-device-grid-uptime">{formatUptime(device.uptime, 2)  || '—'}</div>
      </div>
    );
  }

  // Mobile device card - matches mockup 8b's card anatomy 1:1: a name
  // + state-badge header row, a chip row (device type / management or
  // service label / a short meta string), then - Peplink only - the
  // offline-alert strips, the 4-up CLIENTS/IMPORTANT/WAN/IMP WAN grid,
  // and the uptime line, exactly as already built for the desktop grid
  // row above (same secondaryChip/metaLine logic, same ft-badge classes
  // for state, same alert strips and stat fields). Starlink cards
  // (managed or roam) stop after the chip row - the mockup shows a
  // data-plan usage bar on the managed card, but there's no per-device
  // plan-usage field in the API yet (same honesty rule as the desktop
  // 24H DATA column and the missing "Over Plan" filter above), so
  // nothing is faked there.
  const secondaryChip = device.device_type === 'peplink'
    ? (MGMT_LABEL[device.peplinkManagementType] || null)
    : (isRoam ? 'ROAM' : isMissing ? 'MISSING' : device.service === 'managed' ? 'MARITIME' : null);

  const metaLine = device.device_type === 'peplink'
    ? [device.serial_number, device.model].filter(Boolean).join(' · ')
    : (device.connected_via_peplink ? `via ${device.connected_via_peplink.slice(-4)}${device.wan_interface_name ? ` (${device.wan_interface_name})` : ''}` : (device.serial_number || ''));

  return (
    <div
      className={'sd-device' + (isOpen ? ' sd-device-open' : '') + (isClickable ? ' sd-device-clickable' : '')}
      data-tour={tourId}
      style={{ borderLeft: `2px solid ${statusColor}` }}
      onClick={isClickable ? onClick : undefined}
    >
      <div className="sd-device-header-row">
        <span className="sd-device-mobile-name" title={device.name}>{formatStarlinkTerminalId(device.name)}</span>
        {stateBadge}
      </div>

      <div className="sd-device-meta-row">
        <span className="sd-device-chip">{device.device_type === 'peplink' ? 'PEPLINK' : 'STARLINK'}</span>
        {secondaryChip && <span className="sd-device-chip">{secondaryChip}</span>}
        {metaLine && <span className="sd-device-meta-text">{metaLine}</span>}
      </div>

      {device.device_type === 'starlink' && device.plan_usage && (
        <StarlinkUsageBar device={device} mobile />
      )}

      {isPeplinkMobile && (
        <>
          {device.offline_important_clients_count > 0 && (
            <div className="sd-mobile-alert-strip offline-alert">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#112023" strokeWidth="1.8">
                <path d="M8 2.5L14.5 13.5H1.5z"></path><path d="M8 6.5v3.2"></path><path d="M8 11.4v.6"></path>
              </svg>
              <span>{device.offline_important_clients_count} important client{device.offline_important_clients_count > 1 ? 's' : ''} offline</span>
            </div>
          )}
          {device.offline_important_wan_count > 0 && (
            <div className="sd-mobile-alert-strip wan-alert">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="var(--color-accent)" strokeWidth="1.8">
                <path d="M8 2.5L14.5 13.5H1.5z"></path><path d="M8 6.5v3.2"></path><path d="M8 11.4v.6"></path>
              </svg>
              <span>{device.offline_important_wan_count} important WAN{device.offline_important_wan_count > 1 ? 's' : ''} offline</span>
            </div>
          )}

          <div className="sd-mobile-stats-grid" data-tour={tourId ? `${tourId}-stats` : undefined}>
            <div
              className="sd-mobile-stat-cell sd-mobile-stat-clickable"
              onClick={(e) => { e.stopPropagation(); onOpenMode && onOpenMode('clients'); }}
            >
              <div className="sd-mobile-stat-value">{device.client_count || 0}</div>
              <div className="sd-mobile-stat-label">Clients</div>
            </div>
            <div
              className="sd-mobile-stat-cell sd-mobile-stat-clickable"
              onClick={(e) => { e.stopPropagation(); onOpenMode && onOpenMode('clients'); }}
            >
              <div className="sd-mobile-stat-value" style={{ color: 'var(--color-secondary)' }}>{device.important_clients_count || 0}</div>
              <div className="sd-mobile-stat-label">Important</div>
            </div>
            <div
              className="sd-mobile-stat-cell sd-mobile-stat-clickable"
              onClick={(e) => { e.stopPropagation(); onOpenMode && onOpenMode('wan'); }}
            >
              <div className="sd-mobile-stat-value">{device.wan_interface_count || 0}</div>
              <div className="sd-mobile-stat-label">WAN</div>
            </div>
            <div
              className="sd-mobile-stat-cell sd-mobile-stat-clickable"
              onClick={(e) => { e.stopPropagation(); onOpenMode && onOpenMode('wan'); }}
            >
              <div className="sd-mobile-stat-value" style={{ color: 'var(--color-secondary)' }}>{device.important_wan_count || 0}</div>
              <div className="sd-mobile-stat-label">Imp WAN</div>
            </div>
          </div>

          {(device.uptime || device.usage_24h_mb != null) && (
            <div className="sd-mobile-uptime">
              {[
                device.uptime && `up ${formatUptime(device.uptime, 3)}`,
                device.usage_24h_mb != null && `24h ${formatUsageMb(device.usage_24h_mb)}`,
              ].filter(Boolean).join(' · ')}
            </div>
          )}
        </>
      )}

      {device.device_type === 'starlink' && (device.uptime || device.usage_24h_mb != null) && (
        <div className="sd-mobile-uptime">
          {[
            device.uptime && `up ${formatUptime(device.uptime, 3)}`,
            device.usage_24h_mb != null && `24h ${formatUsageMb(device.usage_24h_mb)}`,
          ].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}

// Desktop: the panel stays in view (sticky) and is as tall as the screen,
// minus the fleet header if that header is pinned to the top. Mobile:
// unchanged - normal panel in the page flow.
// Clicking anywhere on the panel opens the Alert Dashboard.
const EVENT_FEED_GAP = 16; // px of breathing room above/below the panel

function useStickyHeaderHeight(enabled) {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const measure = () => {
      const header = document.querySelector('.ft-header');
      if (!header) { setHeight(0); return; }
      const pos = window.getComputedStyle(header).position;
      setHeight(pos === 'fixed' || pos === 'sticky' ? header.getBoundingClientRect().height : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [enabled]);
  return height;
}

function EventFeedSidebar({ isMobile }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [err, setErr] = useState(false);
  const headerHeight = useStickyHeaderHeight(!isMobile);

  const openAlerts = () => navigate('/admin/alerts');
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openAlerts();
    }
  };

  const top = headerHeight + EVENT_FEED_GAP;
  const desktopPanelStyle = isMobile ? undefined : {
    position: 'sticky',
    top,
    height: `calc(100vh - ${top + EVENT_FEED_GAP}px)`,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
  };
  // Scroll area fills whatever is left under the panel title.
  const desktopScrollStyle = isMobile ? undefined : {
    flex: 1,
    minHeight: 0,
    maxHeight: 'none',
    overflowY: 'auto',
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const json = await apiFetchJSON('/api/v2/admin/event-feed?limit=20');
        if (!cancelled) { setEvents(json.events || []); setErr(false); }
      } catch {
        if (!cancelled) setErr(true);
      }
    }
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  // "disconnected" -> "Disconnected"
  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  const describe = (e) => {
    if (e.kind === 'starlink_overage') {
      return {
        title: `${e.wan_name} · ${e.alert_type === 'download_overage' ? 'Download' : 'Upload'} overage`,
        detail: e.detail || '',
      };
    }
    return {
      title: e.label,
      detail: `${capitalize(e.from_status) || '?'} → ${capitalize(e.to_status) || '?'}`,
    };
  };

  return (
    <div
      className="ft-sidebar-panel"
      data-tour="sd-events"
      role="link"
      tabIndex={0}
      title="Open Alert Dashboard"
      aria-label="Live Event Feed - open Alert Dashboard"
      onClick={openAlerts}
      onKeyDown={onKeyDown}
      style={isMobile ? { cursor: 'pointer' } : desktopPanelStyle}
    >
      <div className="ft-sidebar-head">
        <span>Live Event Feed</span>
        <i className="fas fa-chevron-right" aria-hidden="true" style={{ opacity: 0.5, fontSize: 10 }}></i>
      </div>
      <div className="ft-sidebar-scroll" style={desktopScrollStyle}>
        {err && <div className="ft-sidebar-empty">Couldn't load events</div>}
        {!err && events.length === 0 && <div className="ft-sidebar-empty">No recent events</div>}
        {events.map((e, i) => {
          const { title, detail } = describe(e);
          return (
            <div key={i} className="ft-event-row">
              <div className="ft-event-time">{formatTime(e.time)}</div>
              <div>
                <div className="ft-event-title">{title}</div>
                <div className="ft-event-detail">{detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CounterFilterBar({ summary, summaryLoading, totalSites, sitesDownCount, attentionSitesCount, activeFilter, onFilterClick }) {
  const pep = summary?.peplink || {};
  const star = summary?.starlink || {};

  const peplinkTotal =
    (pep.total_fully_managed || 0) + (pep.total_wan_managed || 0) + (pep.total_data_center || 0);
  const peplinkOffline =
    (pep.offline_fully_managed || 0) + (pep.offline_wan_managed || 0) + (pep.offline_data_center || 0);
  const peplinkOnline = peplinkTotal - peplinkOffline;

  return (
    <div className="ft-stat-row" data-tour="sd-stats">
      <div className="ft-stat-card">
        <div className="ft-stat-label">Sites Monitored</div>
        <div className="ft-stat-value">{totalSites}</div>
      </div>

      <div className="ft-stat-card">
        <div className="ft-stat-label">Peplink Online</div>
        {summaryLoading ? (
          <div className="ft-stat-value ft-stat-loading">···</div>
        ) : (
          <>
            <div className="ft-stat-value ft-value-secondary">
              {peplinkOnline}<span className="ft-stat-sub">/ {peplinkTotal}</span>
            </div>
            <div className="ft-segment-bar">
              {pep.total_fully_managed > 0 && (
                <span
                  className="ft-segment-online"
                  style={{ flex: pep.total_fully_managed - (pep.offline_fully_managed || 0) }}
                  title="Fully Managed online"
                />
              )}
              {pep.total_wan_managed > 0 && (
                <span
                  className="ft-segment-attention"
                  style={{ flex: pep.total_wan_managed - (pep.offline_wan_managed || 0) }}
                  title="WAN Managed online"
                />
              )}
              {peplinkOffline > 0 && (
                <span className="ft-segment-down" style={{ flex: peplinkOffline }} title="Offline" />
              )}
            </div>
          </>
        )}
      </div>

      <div className="ft-stat-card">
        <div className="ft-stat-label">Starlink Maritime Kits Online</div>
        {summaryLoading ? (
          <div className="ft-stat-value ft-stat-loading">···</div>
        ) : (
          <>
            <div className="ft-stat-value ft-value-secondary">
              {(star.total_managed || 0) - (star.offline_managed || 0)}
              <span className="ft-stat-sub">/ {star.total_managed || 0}</span>
            </div>
            <div className="ft-stat-note">
              {star.total_service_lines != null
                ? `${star.total_service_lines} service line${star.total_service_lines === 1 ? '' : 's'}`
                : '—'}
              {' · '}{star.total_roam || 0} roam / no line
              {star.total_missing > 0 && <>{' · '}{star.total_missing} missing</>}
            </div>
          </>
        )}
      </div>

      <div
        className={'ft-stat-card ft-alert' + (activeFilter === 'needs_attention' ? ' ft-stat-active' : '')}
        onClick={() => !summaryLoading && onFilterClick('needs_attention')}
        role="button"
        style={{ cursor: summaryLoading ? 'default' : 'pointer' }}
      >
        <div className="ft-stat-label">Sites Needing Attention</div>
        {summaryLoading ? (
          <div className="ft-stat-value ft-stat-loading">···</div>
        ) : (
          <>
            <div className="ft-stat-value ft-value-accent">
              {attentionSitesCount}<span className="ft-stat-sub">sites</span>
            </div>
            <div className="ft-stat-note">degraded or partly offline</div>
          </>
        )}
      </div>

      <div
        className={'ft-stat-card ft-critical' + (activeFilter === 'sites_down' ? ' ft-stat-active' : '')}
        onClick={() => onFilterClick('sites_down')}
        role="button"
        style={{ cursor: 'pointer' }}
      >
        <div className="ft-stat-label">Sites Down</div>
        <div className="sd-stat-flag-row">
          <span className="ft-stat-value">{sitesDownCount}</span>
          {sitesDownCount > 0 && <span className="ft-badge ft-badge-down">ALL DEVICES OFF</span>}
        </div>
      </div>
    </div>
  );
}
